import type {
  AssociatedVehicleInside,
  VehicleInside,
  VehicleInsideDriver,
  VehicleInsideRole,
} from "../../../../../passapp-sdk/types/userTypes";

/**
 * Una unidad dentro del complejo, lista para renderizar.
 *
 * `getVehiclesInside` ya devuelve una fila por unidad —con su rol, el estado del chofer y las otras
 * unidades del mismo evento—, así que acá no se deriva nada: sólo se resuelve el nombre de la
 * empresa y se arma una key estable.
 *
 * Todas las unidades del listado están dentro: las que salen desaparecen.
 */
export interface VehicleInsideRow {
  key: string;
  plate: string;
  role: VehicleInsideRole;
  /** Snapshot del nombre de carrocería que guardó el evento, no un id del catálogo. */
  vehicleTypeName?: string | null;
  fecha: string;
  timestamp: number;
  driver?: VehicleInsideDriver;
  /** Autoritativo: lo resuelve el backend contra el último evento de la persona. */
  driverInside: boolean;
  associatedVehicles: AssociatedVehicleInside[];
  transportCompanyName?: string | null;
  /** Empresa anfitriona, con el id ya resuelto a nombre. */
  empresa?: string;
  dock?: string | null;
  eventId?: string;
  /** Evento completo, para el modal "Ver más" y para armar el prefill del egreso. */
  event?: any;
}

const eventIdOf = (vehicle: VehicleInside): string | undefined =>
  typeof vehicle.event === "string" ? vehicle.event : vehicle.event?.id;

/**
 * Mapea la respuesta cruda a filas de tabla.
 *
 * @param companyName resuelve el id de empresa a nombre contra `authStore.companies`.
 */
export const mapVehiclesInside = (
  vehicles: VehicleInside[],
  companyName: (companyId?: string) => string
): VehicleInsideRow[] =>
  (vehicles ?? []).map((vehicle, index) => {
    const eventId = eventIdOf(vehicle);
    return {
      // El par evento + patente identifica la unidad; el índice cubre datos incompletos.
      key: `${eventId ?? index}:${vehicle.plate ?? index}`,
      plate: vehicle.plate,
      role: vehicle.role ?? "",
      vehicleTypeName: vehicle.vehicleTypeName,
      fecha: vehicle.datetime,
      timestamp: vehicle.timestamp,
      driver: vehicle.driver,
      driverInside: Boolean(vehicle.driverInside),
      associatedVehicles: vehicle.associatedVehicles ?? [],
      transportCompanyName: vehicle.transportCompanyName,
      empresa: companyName(vehicle.company),
      dock: vehicle.dock,
      eventId,
      event: typeof vehicle.event === "string" ? undefined : vehicle.event,
    };
  });

/** Nombre completo del chofer, o undefined si el evento no trajo ninguno. */
export const driverName = (driver?: VehicleInsideDriver): string | undefined => {
  const name = [driver?.firstname, driver?.lastname].filter(Boolean).join(" ").trim();
  return name || undefined;
};

const normalize = (value?: string | null): string =>
  (value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

/**
 * Clasificación por nombre de carrocería. Sólo se usa como plan B cuando el backend manda
 * `role: ""`, que no distingue un camión suelto de un utilitario. El catálogo de carrocerías lo
 * administra el edificio, por eso la comparación es difusa.
 */
export const nombreEsTractor = (vehicleTypeName?: string | null): boolean =>
  /tractor|camion/.test(normalize(vehicleTypeName));

export const nombreEsAcoplado = (vehicleTypeName?: string | null): boolean =>
  /acoplado|semi|trailer|batea/.test(normalize(vehicleTypeName));

export const nombreEsMoto = (vehicleTypeName?: string | null): boolean =>
  /moto/.test(normalize(vehicleTypeName));

export const nombreEsAuto = (vehicleTypeName?: string | null): boolean =>
  /\bauto|sedan|coupe/.test(normalize(vehicleTypeName));

export const nombreEsFurgon = (vehicleTypeName?: string | null): boolean =>
  /furgon|camioneta|utilitario|pickup|van/.test(normalize(vehicleTypeName));

/** `role` es autoritativo; el nombre sólo desempata cuando viene vacío. */
export const esTractor = (row: {
  role?: VehicleInsideRole;
  vehicleTypeName?: string | null;
}): boolean => (row.role ? row.role === "tractor" : nombreEsTractor(row.vehicleTypeName));

export const esAcoplado = (row: {
  role?: VehicleInsideRole;
  vehicleTypeName?: string | null;
}): boolean => (row.role ? row.role === "trailer" : nombreEsAcoplado(row.vehicleTypeName));

/** El `role` del backend sólo distingue tractor/acoplado: para el resto de las carrocerías
 * (auto, moto, furgón…) la única señal es el nombre del catálogo, sin desempate por rol. */
export const esMoto = (row: { vehicleTypeName?: string | null }): boolean =>
  nombreEsMoto(row.vehicleTypeName);

export const esAuto = (row: { vehicleTypeName?: string | null }): boolean =>
  nombreEsAuto(row.vehicleTypeName);

export const esFurgon = (row: { vehicleTypeName?: string | null }): boolean =>
  nombreEsFurgon(row.vehicleTypeName);

/**
 * Qué mostrar en la columna Tipo.
 *
 * Manda el rol, igual que en el icono: lo que el guardia necesita saber de un vistazo es si la
 * unidad tira o si la tiran, y el rol es el dato del backend que lo dice. La carrocería es el
 * nombre que le puso el edificio a su catálogo —"Batea", "Camión Chasis"— y sólo se usa cuando no
 * hay rol, que es el caso de autos, motos y utilitarios. El nombre completo sigue estando en
 * "Ver más".
 *
 * Sin carrocería ni rol la unidad es un vehículo suelto: se muestra "Auto", que es el caso más
 * común, en vez de un guión.
 */
export const tipoLabel = (row: {
  role?: VehicleInsideRole;
  vehicleTypeName?: string | null;
}): string => {
  if (row.role === "trailer") return "Acoplado";
  if (row.role === "tractor") return "Tractor";
  if (row.vehicleTypeName) return row.vehicleTypeName;
  return "Auto";
};

/**
 * Id del usuario que ingresó la unidad.
 *
 * `event.who` no tiene una forma estable: en los payloads que manda el front es el id suelto, pero
 * en las respuestas viene poblado como objeto de usuario (ver `useRegisterExit`, que lo spreadea).
 * Mandarlo crudo a un endpoint que espera un id hace que la llamada no devuelva nada.
 */
export const eventUserId = (row: { driver?: { id?: string }; event?: any }): string | undefined => {
  if (row.driver?.id) return row.driver.id;
  const who = row.event?.who;
  if (!who) return undefined;
  return typeof who === "string" ? who : who.id;
};

/**
 * Acoplados del tractor que quedarían dentro sin tractor si se lo lleva a retirar otra unidad.
 *
 * El guardia elige un tractor que está dentro para sacar un acoplado ajeno; si ese tractor tenía a
 * su vez sus propios acoplados adentro, se van a quedar solos. No lo bloqueamos, pero se avisa.
 */
export const acopladosQueQuedanSolos = (
  tractor: VehicleInsideRow | undefined,
  selectedPlates: string[]
): string[] => {
  if (!tractor) return [];
  const yaSeleccionadas = selectedPlates.map((plate) => plate.toUpperCase());
  return (tractor.associatedVehicles ?? [])
    .filter(
      (unit) =>
        unit.inside &&
        unit.plate &&
        unit.role !== "tractor" &&
        !yaSeleccionadas.includes(unit.plate.toUpperCase())
    )
    .map((unit) => unit.plate);
};
